import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Link as MuiLink,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { Delete, Edit } from '@mui/icons-material';
import axios from 'axios';
import DetailBreadcrumbs from '../common/DetailBreadcrumbs';

const SequencingRunDetail = () => {
  const { id } = useParams();
  const [run, setRun] = useState(null);
  const [samples, setSamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all'); // all | linked | review | not_specimen
  const navigate = useNavigate();

  useEffect(() => {
    fetchRunDetails();
  }, [id]);

  const fetchRunDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const [runResponse, samplesResponse] = await Promise.all([
        axios.get(`/api/sequencing/runs/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`/api/sequencing/runs/${id}/samples`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setRun(runResponse.data);
      setSamples(samplesResponse.data);
    } catch (err) {
      console.error('Error fetching run details:', err);
      setError('Failed to load run details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'linked':
        return 'success';
      case 'no_match':
        return 'warning';
      case 'needs_review':
        return 'error';
      case 'not_specimen':
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'linked':
        return 'Linked';
      case 'no_match':
        return 'No match';
      case 'needs_review':
        return 'Needs review';
      case 'not_specimen':
        return 'Not a specimen';
      default:
        return status || 'unknown';
    }
  };

  const toDateInput = (val) => {
    if (!val) return '';
    const d = new Date(val);
    return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
  };

  const openEdit = () => {
    setEditForm({
      run_label: run.run_label || '',
      sequencer_type: run.sequencer_type || '',
      completion_date: toDateInput(run.completion_date),
      pool_name: run.pool_name || '',
      service_request_number: run.service_request_number || '',
      flowcell_id: run.flowcell_id || '',
      base_directory: run.base_directory || '',
      notes: run.notes || '',
    });
    setEditOpen(true);
  };

  const handleEditChange = (field, value) => setEditForm((f) => ({ ...f, [field]: value }));

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`/api/sequencing/runs/${id}`, editForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEditOpen(false);
      await fetchRunDetails();
    } catch (err) {
      console.error('Error updating run:', err);
      setError('Failed to update run');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRun = async () => {
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/sequencing/runs/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      navigate('/sequencing');
    } catch (err) {
      console.error('Error deleting run:', err);
      setError('Failed to delete run');
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  if (loading) {
    return (
      <Container sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!run) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="warning">Run not found</Alert>
      </Container>
    );
  }

  const runIdentifier = run.run_label || run.service_request_number || run.flowcell_id || (run.run_number != null ? `#${run.run_number}` : 'Unknown');

  const REVIEW_STATUSES = ['no_match', 'needs_review'];
  const matchesFilter = (s) =>
    statusFilter === 'all' ? true
    : statusFilter === 'review' ? REVIEW_STATUSES.includes(s.link_status)
    : s.link_status === statusFilter;
  const displayedSamples = samples.filter(matchesFilter);
  const reviewCount = samples.filter(s => REVIEW_STATUSES.includes(s.link_status)).length;

  // A clickable Sample Statistics row that filters the table; click again to clear.
  const statRow = (label, count, color, key) => (
    <Box
      onClick={() => setStatusFilter(prev => (prev === key ? 'all' : key))}
      sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', px: 1, py: 0.5, borderRadius: 1, bgcolor: statusFilter === key ? 'action.selected' : 'transparent', '&:hover': { bgcolor: 'action.hover' } }}
    >
      <Typography variant="body2">{label}:</Typography>
      <Chip label={count} color={color} size="small" />
    </Box>
  );

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <DetailBreadcrumbs
        items={[
          { label: 'Sequencing Runs', to: '/sequencing' },
          { label: `Run ${runIdentifier}` },
        ]}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ flexGrow: 1 }}>
          Sequencing Run: {runIdentifier}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Edit />}
          onClick={openEdit}
          sx={{ mr: 1 }}
        >
          Edit
        </Button>
        <Button
          variant="outlined"
          color="error"
          startIcon={<Delete />}
          onClick={() => setDeleteDialogOpen(true)}
        >
          Delete Run
        </Button>
      </Box>

      {/* Run Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Run Information</Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Service Request:</strong> {run.service_request_number || 'N/A'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  <strong>Flowcell ID:</strong> {run.flowcell_id || 'N/A'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  <strong>Pool Name:</strong> {run.pool_name || 'N/A'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  <strong>Sequencer:</strong> {run.sequencer_type || 'N/A'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  <strong>Completion Date:</strong> {formatDate(run.completion_date)}
                </Typography>
                {run.base_directory && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    <strong>Base Directory:</strong> {run.base_directory}
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Sample Statistics</Typography>
              <Typography variant="caption" color="text.secondary">Click a row to filter the table below.</Typography>
              <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {statRow('Total Samples', samples.length, 'primary', 'all')}
                {statRow('Linked', samples.filter(s => s.link_status === 'linked').length, 'success', 'linked')}
                {statRow('Review', reviewCount, 'warning', 'review')}
                {statRow('Not a specimen', samples.filter(s => s.link_status === 'not_specimen').length, 'default', 'not_specimen')}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Notes Section */}
      {run.notes && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Notes</Typography>
          <Typography variant="body2">{run.notes}</Typography>
        </Paper>
      )}

      {/* Samples Table */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h6">Samples{statusFilter !== 'all' ? ` — showing ${displayedSamples.length} of ${samples.length}` : ''}</Typography>
          {statusFilter !== 'all' && <Button size="small" onClick={() => setStatusFilter('all')}>Show all</Button>}
        </Box>

        {samples.length === 0 ? (
          <Alert severity="info">No samples found for this run.</Alert>
        ) : displayedSamples.length === 0 ? (
          <Alert severity="info">No samples match this filter.</Alert>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>WUID</TableCell>
                  <TableCell>Library ID</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Specimen</TableCell>
                  <TableCell>FASTQ Files</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedSamples.map((sample) => (
                  <TableRow
                    key={sample.id}
                    hover
                    sx={{ cursor: sample.specimen_id ? 'pointer' : 'default' }}
                    onClick={() => sample.specimen_id && navigate(`/specimens/${sample.specimen_id}`)}
                  >
                    <TableCell>{sample.wuid || 'N/A'}</TableCell>
                    <TableCell>{sample.library_id || 'N/A'}</TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(sample.link_status)}
                        color={getStatusColor(sample.link_status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {sample.specimen_id
                        ? 'Linked'
                        : sample.sample_attributes && sample.sample_attributes.suspect_specimen_number
                          ? `Not linked — extracted #${sample.sample_attributes.suspect_specimen_number}`
                          : 'Not Linked'}
                    </TableCell>
                    <TableCell>
                      {sample.fastq_r1_path && (
                        <Typography variant="caption" display="block">
                          R1: {sample.fastq_r1_path.split('/').pop()}
                        </Typography>
                      )}
                      {sample.fastq_r2_path && (
                        <Typography variant="caption" display="block">
                          R2: {sample.fastq_r2_path.split('/').pop()}
                        </Typography>
                      )}
                      {!sample.fastq_r1_path && !sample.fastq_r2_path && 'N/A'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Edit Run Dialog */}
      <Dialog open={editOpen} onClose={() => !saving && setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Run</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Run label" value={editForm.run_label || ''} onChange={(e) => handleEditChange('run_label', e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Sequencer" value={editForm.sequencer_type || ''} onChange={(e) => handleEditChange('sequencer_type', e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="date" label="Completion date" value={editForm.completion_date || ''} onChange={(e) => handleEditChange('completion_date', e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Pool name" value={editForm.pool_name || ''} onChange={(e) => handleEditChange('pool_name', e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Service request #" value={editForm.service_request_number || ''} onChange={(e) => handleEditChange('service_request_number', e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Flowcell ID" value={editForm.flowcell_id || ''} onChange={(e) => handleEditChange('flowcell_id', e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Base directory" value={editForm.base_directory || ''} onChange={(e) => handleEditChange('base_directory', e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline minRows={2} label="Notes" value={editForm.notes || ''} onChange={(e) => handleEditChange('notes', e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Run label, service request #, and flowcell ID are used to match a re-import to this run.
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Sequencing Run?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this sequencing run ({runIdentifier})?
            This will also delete all {samples.length} associated samples. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDeleteRun} color="error" disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SequencingRunDetail;